import React from 'react';
import styled from 'styled-components';
import { GqlImageBanner, GqlImageBannerPlatform, ImageBannerPlatformType, ImageBannerType } from 'types/graphql';
import { Hyperlink as HyperlinkIcon } from 'src/svg/hyperlink';

import { displayBannerPlatforms } from '../../image-banner-modal/image-banner-modal.helper';
import { LinkContainer } from './image-banner-list-item.styles';

type ImageBannerLinksProps = {
  banner: GqlImageBanner;
  hasMobileApp: boolean;
  showLinkText?: boolean;
};

/**
 * Get the display label for a connected banner type
 */
function getConnectionLabel(bannerType: ImageBannerType): string {
  switch (bannerType) {
    case ImageBannerType.special:
      return 'Connected to Special';
    case ImageBannerType.collection:
      return 'Connected to Collection';
    case ImageBannerType.category:
      return 'Connected to Custom Category';
    default:
      return '';
  }
}

/**
 * Get enabled platforms for display
 */
function getEnabledPlatforms(
  banner: GqlImageBanner,
  hasMobileApp: boolean
): { label: string; type: ImageBannerPlatformType }[] {
  const { link, platforms } = banner;
  const enabledPlatforms: { label: string; type: ImageBannerPlatformType }[] = [];

  Object.values(displayBannerPlatforms).forEach((item) => {
    const platform: GqlImageBannerPlatform = platforms?.find((p) => p?.type === item.value) ?? {
      enabled: true,
      link,
      openInNewTab: true,
      type: item.value,
    };

    if (!platform.enabled) {
      return;
    }

    if (platform.type === ImageBannerPlatformType.iosMobileApp && !hasMobileApp) {
      return;
    }

    enabledPlatforms.push({ label: item.label, type: platform.type });
  });

  return enabledPlatforms;
}

export function ImageBannerLinks({ banner, hasMobileApp, showLinkText = true }: ImageBannerLinksProps): JSX.Element {
  const { link, platforms, bannerType } = banner;
  const isConnectedBanner = bannerType && bannerType !== ImageBannerType.link;

  // For connected banners, show connection info and enabled platforms
  if (isConnectedBanner) {
    const enabledPlatforms = getEnabledPlatforms(banner, hasMobileApp);
    const platformLabels = enabledPlatforms.map((p) => p.label).join(', ');

    return (
      <PlatformLinks>
        <ConnectionInfo>
          <ConnectionBadge $type={bannerType}>{getConnectionLabel(bannerType)}</ConnectionBadge>
          {platformLabels && <PlatformList>Enabled on: {platformLabels}</PlatformList>}
        </ConnectionInfo>
      </PlatformLinks>
    );
  }

  // For link banners, show the existing platform links UI
  return (
    <PlatformLinks>
      {Object.values(displayBannerPlatforms).map((item) => {
        const platform: GqlImageBannerPlatform = platforms?.find((p) => p?.type === item.value) ?? {
          enabled: true,
          link,
          openInNewTab: true,
          type: item.value,
        };

        if (!platform.enabled) {
          return null;
        }

        if (platform.type === ImageBannerPlatformType.iosMobileApp && !hasMobileApp) {
          return null;
        }

        return (
          <PlatformLinkContainer key={platform.type}>
            <b>{item.label}</b>
            {platform.link ? (
              <a href={platform.link} target='_blank' rel='noreferrer'>
                <HyperlinkIcon />
                {showLinkText ? ' View link' : ''}
              </a>
            ) : (
              <>
                <HyperlinkIcon />
                {showLinkText ? ' No link' : ''}
              </>
            )}
          </PlatformLinkContainer>
        );
      })}
    </PlatformLinks>
  );
}

const PlatformLinks = styled.div`
  display: flex;
  flex-grow: 1;
`;

const PlatformLinkContainer = styled(LinkContainer)`
  border-right: 1px solid #d9d9d9;
  flex-grow: 0;
  font-weight: normal;
  line-height: 1;
  margin-right: 20px;
  padding-right: 20px;

  &:last-child {
    border-right: none;
    padding-right: 0;
    margin-right: 0;
  }

  a {
    align-items: center;
    display: flex;
  }

  a,
  b {
    font-weight: 700;
  }

  b,
  svg {
    display: inline-block;
    margin-right: 8px;
    vertical-align: middle;
  }

  svg {
    margin-right: 3px;
  }
`;

const ConnectionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ConnectionBadge = styled.span<{ $type: ImageBannerType }>`
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
  background: ${({ $type }) => {
    switch ($type) {
      case ImageBannerType.special:
        return '#fff3e0';
      case ImageBannerType.collection:
        return '#e3f2fd';
      case ImageBannerType.category:
        return '#f3e5f5';
      default:
        return '#f5f5f5';
    }
  }};
  color: ${({ $type }) => {
    switch ($type) {
      case ImageBannerType.special:
        return '#e65100';
      case ImageBannerType.collection:
        return '#1565c0';
      case ImageBannerType.category:
        return '#7b1fa2';
      default:
        return '#757575';
    }
  }};
`;

const PlatformList = styled.span`
  font-size: 12px;
  color: #6d747b;
`;
